// =============================================================================
// lib/presentation/modules.ts — ce qu'Amiral Blanc fait, pour la présentation.
// UNE seule liste, lue par l'accueil public (LandingView), l'accueil connecté
// (app/page.tsx) et la fenêtre de bienvenue — jamais trois versions qui divergent.
//
// Chaque module porte son ÉTAT RÉEL. Règle (docs/product/presentation-claims-
// control.md) : une capacité change d'état ici seulement sur preuve d'exécution
// réelle, jamais sur code écrit ou RFC rédigée. Vocabulaire : « extraites et
// tracées », jamais « validées » ; « sur notre feuille de route », jamais au présent.
// =============================================================================

/** En service : utilisé pour de vrai. Pilote : fonctionne, encore limité. Feuille de route : pas livré. */
export type ModuleState = 'service' | 'pilote' | 'route'

export const STATE_LABEL: Record<ModuleState, { label: string; color: string; hint: string }> = {
  service: { label: 'En service',       color: '#34d399', hint: 'utilisé en conditions réelles' },
  pilote:  { label: 'Pilote',           color: '#fbbf24', hint: 'fonctionne, périmètre encore limité' },
  route:   { label: 'Feuille de route', color: '#94a3b8', hint: 'pas encore livré' },
}

export type ModuleGroup = 'travail' | 'agents' | 'intelligence'

export interface PresentationModule {
  key:    string
  group:  ModuleGroup
  icon:   string
  name:   string
  pitch:  string            // une phrase, ce que ça fait pour la personne
  points: string[]          // ce qui existe vraiment (ou, pour la feuille de route, ce qui est visé)
  state:  ModuleState
  href?:  string            // où le trouver une fois connecté
}

export const GROUP_LABEL: Record<ModuleGroup, string> = {
  travail:      'Votre travail',
  agents:       'Vos postes et vos agents',
  intelligence: 'Intelligence et territoire',
}

export const MODULES: PresentationModule[] = [
  // ── Votre travail ─────────────────────────────────────────────────────────
  {
    key: 'projets', group: 'travail', icon: '◆', name: 'Projets', state: 'service', href: '/bureau/affaires?tab=projects',
    pitch: 'Le projet est le contexte du travail : il dit ce qu’il doit accomplir.',
    points: ['But obligatoire, cadrage (messages, cible, prochaine étape)', 'Objectifs saisis : cible, unité, échéance', 'Partage entre Bureaux, échanges rattachés'],
  },
  {
    key: 'crm', group: 'travail', icon: '◎', name: 'CRM', state: 'service', href: '/bureau/contacts',
    pitch: 'Vos clients, entreprises et relations, avec le contexte de chaque relation.',
    points: ['Contacts, entreprises, relations client / fournisseur / partenaire', 'Historique des échanges par contact et par entreprise', 'Homonymes et nouvelles adresses : proposés, jamais décidés seuls'],
  },
  {
    key: 'courriels', group: 'travail', icon: '✉', name: 'Courriels', state: 'pilote', href: '/bureau/courriers',
    pitch: 'Vos courriels replacés dans leur contexte : contact, entreprise, projet.',
    points: ['Gmail lu par votre poste, jamais par le serveur', 'Import de fichiers .eml', 'Rattachement à un projet sur votre décision'],
  },
  {
    key: 'messages', group: 'travail', icon: '▤', name: 'Messages et équipes', state: 'service', href: '/messages',
    pitch: 'Conversations personnelles et canaux d’équipe, séparés et durables.',
    points: ['Équipes et canaux par organisation', 'Un départ coupe l’accès, jamais l’historique', 'Traduction à la demande, l’original est conservé'],
  },
  {
    key: 'bureau', group: 'travail', icon: '▣', name: 'Bureau et fichiers', state: 'service', href: '/bureau/files',
    pitch: 'L’espace de travail d’une équipe : fichiers, tâches, membres.',
    points: ['Fichiers synchronisés avec vos postes', 'Membres et partages contrôlés', 'Dossier par projet : sur notre feuille de route'],
  },
  {
    key: 'documents', group: 'travail', icon: '◫', name: 'Documents intelligents', state: 'pilote',
    pitch: 'Un document détecté est analysé, puis une proposition attend votre validation.',
    points: ['Détecté → analysé → proposé → validé', 'Chaque proposition cite ce sur quoi elle s’appuie', 'Traitement sur le poste d’abord, le serveur en relais'],
  },

  // ── Vos postes et vos agents ──────────────────────────────────────────────
  {
    key: 'agent-poste', group: 'agents', icon: '⬡', name: 'Agent de poste', state: 'service', href: '/parametres?tab=agents',
    pitch: 'Votre ordinateur devient une ressource de travail, sous votre contrôle.',
    points: ['Inventaire matériel et logiciel, diagnostic réseau', 'Chaque capacité est approuvée par vous, jamais accordée en silence', 'Windows ; Linux pour l’inventaire'],
  },
  {
    key: 'calcul', group: 'agents', icon: '◈', name: 'Calcul partagé (GPU)', state: 'pilote', href: '/parametres?tab=agents',
    pitch: 'Les postes équipés prêtent leur carte graphique aux traitements IA.',
    points: ['Répartition selon la mémoire et les modèles disponibles', 'Repli sur le serveur quand aucun poste n’est disponible'],
  },
  {
    key: 'ticoune', group: 'agents', icon: '🦋', name: 'Ti-Coune, l’assistant', state: 'service', href: '/messages',
    pitch: 'Un assistant en français qui interroge les modules à votre place.',
    points: ['Recherche, règlements, marchés, documents', 'Réponses diffusées en direct'],
  },
  {
    key: 'missions', group: 'agents', icon: '⚑', name: 'Missions autonomes', state: 'route',
    pitch: 'Des agents qui exécutent un plan validé de bout en bout.',
    points: ['Sur notre feuille de route', 'Aujourd’hui : les agents proposent, vous validez'],
  },
  {
    key: 'campagnes', group: 'agents', icon: '✦', name: 'Campagnes de courriels', state: 'route',
    pitch: 'Préparer, valider puis envoyer une campagne à partir d’un projet.',
    points: ['Sur notre feuille de route', 'Consentement et désabonnement prouvés, validation obligatoire'],
  },

  // ── Intelligence et territoire ────────────────────────────────────────────
  {
    key: 'recherche', group: 'intelligence', icon: '⌕', name: 'Recherche augmentée', state: 'service', href: '/search',
    pitch: 'Plusieurs sources fusionnées en une réponse, en quelques secondes.',
    points: ['Web, index et mémoire, priorité aux sources gouvernementales', 'Une source en panne n’arrête pas les autres'],
  },
  {
    key: 'legal', group: 'intelligence', icon: '⚖', name: 'Règlements municipaux', state: 'pilote', href: '/lawyer',
    pitch: 'Retrouve la règle de zonage applicable et sa source.',
    points: ['Règles extraites et tracées jusqu’à leur source', 'Dit « je ne peux pas prouver cette valeur » quand la source est ambiguë', 'Validation humaine requise — ne remplace ni urbaniste ni avocat'],
  },
  {
    key: 'villes', group: 'intelligence', icon: '◍', name: 'Fiche municipale 360°', state: 'service', href: '/technologue/villes',
    pitch: 'Une municipalité vue d’un coup : divisions, services, données publiques.',
    points: ['ISQ, Statistique Canada, INSPQ, ministère de l’Éducation', 'Carte interactive'],
  },
  {
    key: 'spatial', group: 'intelligence', icon: '▲', name: 'Enveloppe constructible 3D', state: 'pilote', href: '/technologue',
    pitch: 'Ce qu’une parcelle permet de bâtir, en volume.',
    points: ['Première version : zone, marges, hauteur', 'Contraintes environnementales en ajout'],
  },
  {
    key: 'bourse', group: 'intelligence', icon: '◬', name: 'Bourse et marchés', state: 'service', href: '/bourse',
    pitch: 'Analyse technique et sentiment, pour s’informer.',
    points: ['Indicateurs et signaux expliqués', 'Information, pas un conseil financier'],
  },
  {
    key: 'processus', group: 'intelligence', icon: '↻', name: 'Processus et amélioration', state: 'route',
    pitch: 'Cartographier un processus, trouver les causes, mesurer le résultat.',
    points: ['Sur notre feuille de route, dans le module Projet', 'Value Stream Mapping, 5 Pourquoi'],
  },
]

/** Le principe, partout le même. */
export const PRINCIPLE = ['L’agent propose.', 'La personne valide.', 'Le système exécute.'] as const

/** Commencer petit. */
export const START_PATH = ['un Bureau', 'un projet', 'un dossier', 'un agent', 'une tâche'] as const

export const TAGLINE = 'Votre environnement de travail intelligent.'
export const SUBLINE = 'Reliez vos clients, projets, documents, communications et agents pour transformer le travail en actions et en résultats.'
